import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useSipContext } from '@/context/SipContext';

interface SipConnectionStatusProps {
  variant?: 'inline' | 'banner' | 'minimal';
  showReconnectButton?: boolean;
}

export const SipConnectionStatus: React.FC<SipConnectionStatusProps> = ({
  variant = 'banner',
  showReconnectButton = true
}) => {
  const { status, connect, refreshCredentials, isLoadingCredentials, credentialsError } = useSipContext();

  // Don't show anything if everything is working properly
  if (status.isConnected && status.isRegistered && !status.error && !credentialsError) {
    return null;
  }

  // Show loading state
  if (isLoadingCredentials) {
    return (
      <Alert className="border-blue-200 bg-blue-50">
        <Loader2 className="h-4 w-4 animate-spin" />
        <AlertDescription className="text-blue-800">
          Loading VoIP credentials...
        </AlertDescription>
      </Alert>
    );
  }

  // Show credentials error
  if (credentialsError) {
    return (
      <Alert className="border-red-200 bg-red-50">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-red-800 flex items-center justify-between">
          <span>VoIP Error: {credentialsError}</span>
          {showReconnectButton && (
            <Button
              size="sm"
              variant="outline"
              onClick={refreshCredentials}
              className="ml-2"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Show connection status
  const getStatusMessage = () => {
    if (status.isConnecting) {
      return "Connecting to VoIP server...";
    }
    if (!status.isConnected) {
      return `VoIP connection failed${status.connectionAttempts > 1 ? ` (${status.connectionAttempts} attempts)` : ''}`;
    }
    if (status.isConnected && !status.isRegistered) {
      return "Connected but not registered - check credentials";
    }
    if (status.error) {
      return `VoIP Error: ${status.error}`;
    }
    return "VoIP connection issue";
  };

  const getStatusIcon = () => {
    if (status.isConnecting) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    if (!status.isConnected) {
      return <PhoneOff className="h-4 w-4" />;
    }
    if (status.error) {
      return <AlertTriangle className="h-4 w-4" />;
    }
    return <Phone className="h-4 w-4" />;
  };

  const getAlertVariant = () => {
    if (status.isConnecting) {
      return "border-blue-200 bg-blue-50";
    }
    if (status.error || !status.isConnected) {
      return "border-red-200 bg-red-50";
    }
    return "border-yellow-200 bg-yellow-50";
  };

  const getTextColor = () => {
    if (status.isConnecting) {
      return "text-blue-800";
    }
    if (status.error || !status.isConnected) {
      return "text-red-800";
    }
    return "text-yellow-800";
  };

  if (variant === 'minimal') {
    return (
      <div className="flex items-center gap-2 text-sm">
        {getStatusIcon()}
        <span className={getTextColor()}>{getStatusMessage()}</span>
        {showReconnectButton && !status.isConnecting && (
          <Button
            size="sm"
            variant="ghost"
            onClick={connect}
            className="h-6 w-6 p-0"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <Alert className={getAlertVariant()}>
      {getStatusIcon()}
      <AlertDescription className={`${getTextColor()} flex items-center justify-between`}>
        <span>{getStatusMessage()}</span>
        {showReconnectButton && !status.isConnecting && (
          <Button
            size="sm"
            variant="outline"
            onClick={connect}
            className="ml-2"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Reconnect
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
};